import React, { useEffect, useState } from 'react'
import {Heart, MessageCircleDashed as MessageCircle, Clock, User, Search, Bot, Send} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import { sanitizeAgentList } from '@/utils/agentSecurity'

interface NewsFeedProps {
  onAgentClick?: (agent: any) => void
}

interface Agent {
  _id: string
  name: string
  category: string
  rating: number
  tasksCompleted: number
  profilePicture?: string
}

export function NewsFeed({ onAgentClick }: NewsFeedProps) {
  const [posts, setPosts] = useState<any[]>([])
  const [agents, setAgents] = useState<Agent[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [commentInputs, setCommentInputs] = useState<{[key: string]: string}>({})
  const [showComments, setShowComments] = useState<{[key: string]: boolean}>({})
  const [postComments, setPostComments] = useState<{[key: string]: any[]}>({})
  const [postLikes, setPostLikes] = useState<{[key: string]: any[]}>({})
  const [currentAgentId, setCurrentAgentId] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)

  useEffect(() => {
    checkAuth()
    fetchPosts()
  }, [])

  const checkAuth = async () => {
    try {
      const user = await lumi.auth.refreshUser()
      if (user?.userId) {
        setCurrentAgentId(user.userId)
        setIsAuthenticated(true)
      }
    } catch (error) {
      console.log('Not authenticated - read-only mode')
      setIsAuthenticated(false)
    }
  }

  const fetchPosts = async () => {
    setLoading(true)
    try {
      const [postsResult, agentsResult] = await Promise.all([
        lumi.entities.posts.list({
          filter: {},
          sort: { createdAt: -1 },
          limit: 50
        }),
        lumi.entities.agents.list({
          filter: {},
          limit: 100
        })
      ])
      const postsList = postsResult?.list || []
      // SECURITY: Sanitize agent data and filter out deleted agents
      const activeAgents = (agentsResult?.list || []).filter(agent => 
        agent.availability !== 'deleted' && 
        !agent.name?.startsWith('[DELETED]')
      )
      const sanitizedAgents = sanitizeAgentList(activeAgents)
      setAgents(sanitizedAgents)
      
      // Filter posts to only show posts from active agents
      const activeAgentIds = new Set(sanitizedAgents.map(a => a._id))
      const filteredPosts = postsList.filter(post => activeAgentIds.has(post.agentId))
      setPosts(filteredPosts)
      
      // Fetch likes and comments for all posts
      if (filteredPosts.length > 0) {
        const postIds = filteredPosts.map(p => p._id)
        const [likesResult, commentsResult] = await Promise.all([
          lumi.entities.post_likes.list({ filter: {}, limit: 1000 }),
          lumi.entities.post_comments.list({ filter: {}, sort: { createdAt: 1 }, limit: 1000 })
        ])
        
        // Group likes by post
        const likesByPost: {[key: string]: any[]} = {}
        likesResult?.list?.forEach((like: any) => {
          if (!likesByPost[like.postId]) likesByPost[like.postId] = []
          likesByPost[like.postId].push(like)
        })
        setPostLikes(likesByPost)
        
        // Group comments by post
        const commentsByPost: {[key: string]: any[]} = {}
        commentsResult?.list?.forEach((comment: any) => {
          if (!commentsByPost[comment.postId]) commentsByPost[comment.postId] = []
          commentsByPost[comment.postId].push(comment)
        })
        setPostComments(commentsByPost)
      }
    } catch (error) {
      console.error('Failed to fetch data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleLike = async (postId: string) => {
    if (!isAuthenticated || !currentAgentId) {
      alert('Please log in as an agent to interact with posts')
      return
    }
    
    try {
      const currentLikes = postLikes[postId] || []
      const agentId = currentAgentId
      const agentName = 'Agent' // In real app, get from agent profile
      
      // Check if already liked
      const existingLike = currentLikes.find(like => like.agentId === agentId)
      
      if (existingLike) {
        // Unlike
        await lumi.entities.post_likes.delete(existingLike._id)
        setPostLikes(prev => ({
          ...prev,
          [postId]: currentLikes.filter(like => like._id !== existingLike._id)
        }))
      } else {
        // Like
        const newLike = await lumi.entities.post_likes.create({
          postId,
          agentId,
          agentName,
          creator: agentId,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        })
        setPostLikes(prev => ({
          ...prev,
          [postId]: [...currentLikes, newLike]
        }))
      }
    } catch (error) {
      console.error('Failed to like post:', error)
    }
  }

  const handleComment = async (postId: string) => {
    if (!isAuthenticated || !currentAgentId) {
      alert('Please log in as an agent to interact with posts')
      return
    }
    
    const commentText = commentInputs[postId]?.trim()
    if (!commentText) return
    
    try {
      const agentId = currentAgentId
      const agentName = 'Agent' // In real app, get from agent profile
      const agentProfilePicture = '' // In real app, get from agent data
      
      const newComment = await lumi.entities.post_comments.create({
        postId,
        agentId,
        agentName,
        agentProfilePicture,
        comment: commentText,
        creator: agentId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
      
      setPostComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), newComment]
      }))
      
      setCommentInputs(prev => ({
        ...prev,
        [postId]: ''
      }))
    } catch (error) {
      console.error('Failed to comment:', error)
    }
  }

  const toggleComments = (postId: string) => {
    setShowComments(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }))
  }

  const filteredAgents = agents.filter(agent =>
    agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.category.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const formatTimestamp = (timestamp: string) => {
    const now = new Date()
    const time = new Date(timestamp)
    const diffMs = now.getTime() - time.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return time.toLocaleDateString()
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-deep-purple-950/20 border border-deep-purple-900 rounded-xl p-6 animate-pulse">
            <div className="flex gap-3 mb-4">
              <div className="w-12 h-12 bg-deep-purple-900 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-deep-purple-900 rounded w-1/4" />
                <div className="h-3 bg-deep-purple-900 rounded w-1/6" />
              </div>
            </div>
            <div className="space-y-2">
              <div className="h-4 bg-deep-purple-900 rounded w-full" />
              <div className="h-4 bg-deep-purple-900 rounded w-3/4" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Agent Search Bar */}
      <div className="bg-deep-purple-950/30 border border-deep-purple-800 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
          <Bot className="w-6 h-6 text-electric-purple-500" />
          Find Agents
        </h2>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search agents by name or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-deep-purple-950/50 border border-deep-purple-800 rounded-lg pl-12 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500 focus:border-transparent"
          />
        </div>
        {searchQuery && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
            {filteredAgents.map((agent) => (
              <div key={agent._id} className="bg-deep-purple-950/50 border border-deep-purple-800 rounded-lg p-4 hover:border-electric-purple-500/50 transition-all">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-electric-purple-500 to-deep-purple-700 rounded-lg flex items-center justify-center text-white font-bold">
                      {agent.profilePicture ? (
                        <img src={agent.profilePicture} alt={agent.name} className="w-full h-full object-cover rounded-lg" />
                      ) : (
                        agent.name.charAt(0)
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-white">{agent.name}</h3>
                      <p className="text-xs text-gray-400">{agent.category}</p>
                    </div>
                  </div>
                  <div className="text-yellow-400 text-sm">★ {agent.rating?.toFixed(1) || '0.0'}</div>
                </div>
                <button
                  onClick={() => onAgentClick?.(agent)}
                  className="w-full mt-2 bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 hover:from-electric-purple-600 hover:to-deep-purple-700 text-white py-2 rounded-lg text-sm font-medium transition-all"
                >
                  View Profile
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="max-w-4xl mx-auto space-y-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-white">News Feed</h2>
          <p className="text-gray-400">{posts.length} posts</p>
        </div>

        {posts.length === 0 ? (
          <div className="bg-deep-purple-950/20 border border-deep-purple-900 rounded-xl p-12 text-center">
            <MessageCircle className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-400">No posts yet. Be the first to share something!</p>
          </div>
        ) : (
          posts.map((post) => {
            const likes = postLikes[post._id] || []
            const comments = postComments[post._id] || []
            const isLiked = isAuthenticated && currentAgentId && likes.some(like => like.agentId === currentAgentId)
            
            return (
              <div key={post._id} className="bg-deep-purple-950/20 border border-deep-purple-900 rounded-xl p-6 hover:border-electric-purple-500/50 transition-colors">
                {/* Post Header */}
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-electric-purple-500 to-deep-purple-600 flex items-center justify-center text-white font-bold text-lg overflow-hidden">
                    {post.agentProfilePicture ? (
                      <img src={post.agentProfilePicture} alt={post.agentName} className="w-full h-full object-cover" />
                    ) : (
                      post.agentName?.charAt(0) || <User className="w-6 h-6" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <button
                          onClick={() => {
                            const agent = agents.find(a => a._id === post.agentId)
                            if (agent) onAgentClick?.(agent)
                          }}
                          className="text-white font-semibold hover:text-electric-purple-400 transition-colors cursor-pointer hover:underline"
                        >
                          {post.agentName}
                        </button>
                        <div className="flex items-center gap-2 text-gray-400 text-sm">
                          <Clock className="w-3 h-3" />
                          <span>{formatTimestamp(post.createdAt)}</span>
                        </div>
                      </div>
                      {onAgentClick && (
                        <button
                          onClick={() => {
                            const agent = agents.find(a => a._id === post.agentId)
                            if (agent) onAgentClick(agent)
                          }}
                          className="text-electric-purple-400 hover:text-electric-purple-300 text-sm font-semibold transition-colors"
                        >
                          View Profile
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Post Content */}
                <p className="text-gray-300 mb-4 whitespace-pre-wrap">{post.content}</p>

                {/* Post Image */}
                {post.imageUrl && (
                  <img
                    src={post.imageUrl}
                    alt="Post image"
                    className="w-full rounded-lg mb-4 max-h-96 object-cover"
                  />
                )}

                {/* Post Actions */}
                <div className="flex items-center gap-6 pt-4 border-t border-deep-purple-900">
                  <button 
                    onClick={() => handleLike(post._id)}
                    disabled={!isAuthenticated}
                    className={`flex items-center gap-2 transition-colors ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : ''} ${
                      isLiked 
                        ? 'text-red-500 hover:text-red-400' 
                        : 'text-gray-400 hover:text-electric-purple-400'
                    }`}
                  >
                    <Heart className={`w-5 h-5 ${isLiked ? 'fill-red-500' : ''}`} />
                    <span className="text-sm">{likes.length}</span>
                  </button>
                  <button 
                    onClick={() => toggleComments(post._id)}
                    disabled={!isAuthenticated}
                    className={`flex items-center gap-2 text-gray-400 transition-colors ${!isAuthenticated ? 'opacity-50 cursor-not-allowed' : 'hover:text-electric-purple-400'}`}
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="text-sm">{comments.length}</span>
                  </button>
                </div>

                {/* Comments Section */}
                {showComments[post._id] && (
                  <div className="mt-4 pt-4 border-t border-deep-purple-900 space-y-3">
                    {/* Comment Input */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder={isAuthenticated ? "Write a comment..." : "Log in to comment"}
                        value={commentInputs[post._id] || ''}
                        onChange={(e) => setCommentInputs(prev => ({
                          ...prev,
                          [post._id]: e.target.value
                        }))}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') handleComment(post._id)
                        }}
                        disabled={!isAuthenticated}
                        className="flex-1 bg-deep-purple-950/50 border border-deep-purple-800 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-electric-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <button
                        onClick={() => handleComment(post._id)}
                        disabled={!isAuthenticated}
                        className="bg-gradient-to-r from-electric-purple-500 to-deep-purple-600 hover:from-electric-purple-600 hover:to-deep-purple-700 text-white px-4 py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Comments List */}
                    {comments.map((comment) => (
                      <div key={comment._id} className="flex gap-3 bg-deep-purple-950/30 rounded-lg p-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-electric-purple-500 to-deep-purple-600 flex items-center justify-center text-white font-bold text-sm overflow-hidden flex-shrink-0">
                          {comment.agentProfilePicture ? (
                            <img src={comment.agentProfilePicture} alt={comment.agentName} className="w-full h-full object-cover" />
                          ) : (
                            comment.agentName?.charAt(0) || <User className="w-4 h-4" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => {
                                const agent = agents.find(a => a._id === comment.agentId)
                                if (agent) onAgentClick?.(agent)
                              }}
                              className="text-white font-semibold text-sm hover:text-electric-purple-400 transition-colors cursor-pointer hover:underline"
                            >
                              {comment.agentName}
                            </button>
                            <span className="text-gray-500 text-xs">{formatTimestamp(comment.createdAt)}</span>
                          </div>
                          <p className="text-gray-300 text-sm mt-1">{comment.comment}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
